<template>
   <div v-if="isClient" class="row">
      <div class="col-12">
         <div :ref="sliderConfig.name" v-swiper="swiperOption" style="overflow:hidden">
            <div class="swiper-wrapper">
               <!-- slides -->
               <div v-for="(box, index) in cards" :key="index" class="swiper-slide">
                  <div
                     :class="[
                        sliderConfig.transparentBg
                           ? core['default-class'].concat('card-transparent')
                           : core['default-class'],
                        sliderConfig.noPadding && 'p-0',
                     ]"
                  >
                     <!-- For rendering Logos -->
                     <div v-if="box.body.type === 'logo'" class="svg logo-container">
                        <div
                           :class="[box.body.class, 'logo mono-logo-container']"
                           :style="getContainerDimension(box.body)"
                        />
                     </div>

                     <div
                        v-if="box.header && !box.header.type"
                        class="card-header"
                        v-html="box.header"
                     />
                     <!-- If wanna render img in header -->
                     <div v-if="box.header && box.header.type === 'img'" class="card-header">
                        <img loading="lazy" :src="box.header.url" alt="Card Image" class="card-img-top" />
                     </div>

                     <div
                        v-if="box.body && box.body.type !== 'logo'"
                        :class="['card-body text-center', sliderConfig.noPadding && 'p-0']"
                     >
                        <!-- For rendering Images -->
                        <ImageContainer v-if="box.body.type === 'img'" :config="box.body" />
                        <!-- For rendering charts -->
                        <div v-if="box.body.type === 'chart'" v-fusioncharts="box.body.options" />

                        <!-- For rendering Image Dashboard -->
                        <div
                           v-if="box.body.type === 'image'"
                           class="dashboard browser-layout"
                           :style="getContainerDimension(box.body)"
                        >
                           <div class="dots" />
                           <ImageContainer :config="box.body" />
                           <h3 class="card-title text-left mt-2">{{ box.body.title }}</h3>
                           <b-link
                              v-if="box.body.url"
                              :to="box.body.url"
                              class="btn btn-secondary-grad shadow"
                           >
                              Learn More
                           </b-link>
                        </div>
                        <!-- For rendering Cards -->
                        <div v-if="box.body.type === 'card'">
                           <div
                              class="card card-bordered slider-card card-hover shadow"
                              @click="goto($event, box.body.item.url)"
                           >
                              <img
                                 :src="
                                    (box.body.item.vectorSource &&
                                       box.body.item.vectorSource.file.url) ||
                                       box.body.item.rasterSource.file.url
                                 "
                                 alt="Card Image"
                                 class="card-img-top"
                                 type="image/svg+xml"
                              />

                              <div class="card-body">
                                 <h4 class="card-title mb-2">
                                    {{
                                       sliderConfig.shortenHeader
                                          ? shorten(box.body.item.header, 50)
                                          : box.body.item.header
                                    }}
                                 </h4>
                              </div>
                           </div>
                        </div>
                        <!-- For rendering Footer -->
                        <div
                           v-if="box.footer"
                           class="card-footer text-center"
                           v-html="box.footer.info"
                        />
                     </div>
                  </div>
               </div>
            </div>
            <!-- Optional controls -->

            <div v-if="sliderConfig.btnPrev" slot="button-prev" class="swiper-button-prev">
               <i class="fc_keyboard_arrow_left circular-button" />
            </div>
            <div v-if="sliderConfig.btnNext" slot="button-next" class="swiper-button-next">
               <i class="fc_keyboard_arrow_right circular-button" />
            </div>
            <div v-if="sliderConfig.scrollbar" slot="scrollbar" class="swiper-scrollbar" />
            <div v-if="sliderConfig.pagination" slot="pagination" class="swiper-pagination" />
         </div>
      </div>
   </div>
</template>

<script>
import ImageContainer from './ImageContainer.vue';

export default {
   name: 'Slider',
   components: {
      ImageContainer,
   },
   directives: {
      fusioncharts: {
         inserted: (el, binding, v) => {
            const config = binding.value;
            const dataUrl = config.dataSource.replace(/[./]/g, '-');

            if (v.context.$store.getters.FC_DS(dataUrl)) {
               const chartData = v.context.$store.getters.FC_DS(dataUrl);
               config.dataFormat = 'json';
               config.dataSource = chartData;
            }
            const obj = new FusionCharts(config).render(el);
            el.setAttribute('data-chartId', obj.id);

            setTimeout(() => {
               if (config.dataFormat !== 'json')
                  v.context.$store.commit('SET_FC_DATASOURCE', {
                     key: dataUrl,
                     data: JSON.stringify(obj.getJSONData()),
                  });
            }, 2000);
         },
         unbind: el => {
            const chrtId = el.getAttribute('data-chartid');
            const chartObj = FusionCharts(chrtId);
            // Remove an Existing Chart
            if (typeof chartObj !== 'undefined') chartObj.dispose();
         },
      },
   },
   props: {
      core: {
         type: Object,
         default: () => ({
            'default-class': ['card'],
         }),
      },

      sliderConfig: {
         type: Object,
         default: () => ({
            pagination: true,
            btnPrev: true,
            btnNext: true,
            scrollbar: false,
            transparentBg: false,
            shortenHeader: false,
            name: Date.now(),
         }),
      },

      swiperOption: {
         type: Object,
         default: () => ({
            slidesPerView: 'auto',
            spaceBetween: 20,
            freeModes: true,
            lazy: true,
            pagination: {
               el: '.swiper-pagination',
               clickable: true,
            },
            navigation: {
               nextEl: '.swiper-button-next',
               prevEl: '.swiper-button-prev',
            },
            breakpoints: {
               1024: {
                  slidesPerView: 4,
                  spaceBetween: 40,
               },
               768: {
                  slidesPerView: 3,
                  spaceBetween: 30,
               },
               640: {
                  slidesPerView: 2,
                  spaceBetween: 20,
               },
               320: {
                  slidesPerView: 1,
                  spaceBetween: 10,
               },
            },
         }),
      },
      cards: {
         type: Array,
         required: false,
         default: () => [], // Array of Objects
      },
   },
   data: () => ({
      isClient: false,
      chartData: [],
   }),
   mounted() {
      this.isClient = true;
   },
   methods: {
      getContainerDimension(ob) {
         return `height: ${ob.height}; width: ${ob.width}`;
      },
      goto(e, url) {
         this.$router.push(url).catch(() => {});
      },
      shorten(text, maxLength) {
         let ret = text;
         if (ret.length > maxLength) {
            ret = `${ret.substr(0, maxLength - 3)}…`;
         }
         return ret;
      },
   },
};
</script>
